/**
 * B5 · Feature flags — production kill-switch backbone.
 *
 * Every high-risk capability enforces through these flags. The cardinal rule
 * is FAIL CLOSED: a missing, unknown, or unreadable flag means the capability
 * is OFF. Never assume a capability is on because we couldn't read state.
 *
 * Pure helpers only — no I/O. The read hook lives in `useFeatureFlags.ts`.
 */

export const FEATURE_FLAGS = [
  'uploads',
  'paid_checkout',
  'public_profiles',
  'chat_media',
  'signup',
  'maintenance_mode',
  'age_gate',
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

export interface FeatureFlagRow {
  key: string;
  enabled: boolean;
}

const KNOWN: ReadonlySet<string> = new Set(FEATURE_FLAGS);

export const isKnownFlag = (key: string): key is FeatureFlag => KNOWN.has(key);

/**
 * Resolve a single flag. Fail closed: enabled ONLY when a row exists for the
 * key with `enabled === true`. Missing row, null input, or a non-boolean
 * `enabled` → false.
 */
export const isFeatureEnabled = (
  rows: readonly FeatureFlagRow[] | null | undefined,
  key: FeatureFlag,
): boolean => {
  if (!rows) return false;
  const row = rows.find((r) => r.key === key);
  return row?.enabled === true;
};

/**
 * Resolve every known flag to a fail-closed boolean map. Unknown rows are
 * ignored; missing flags default to false.
 */
export const deriveFlags = (
  rows: readonly FeatureFlagRow[] | null | undefined,
): Record<FeatureFlag, boolean> => {
  const out = {} as Record<FeatureFlag, boolean>;
  for (const key of FEATURE_FLAGS) out[key] = isFeatureEnabled(rows, key);
  return out;
};

/**
 * Seeded "safe posture" — mirrors the migration seed. The hook uses this ONLY
 * when the flags table is unreadable/unprovisioned (e.g. the migration hasn't
 * been applied to this environment yet), so a read failure falls back to the
 * documented safe posture instead of bricking the app. The risky capabilities
 * (paid_checkout, chat_media) are OFF here, so safety-critical flags still
 * fail safe. Per-flag fail-closed (missing row → off) still applies whenever
 * the table IS readable.
 */
export const DEFAULT_FLAGS: Record<FeatureFlag, boolean> = {
  uploads: true,
  paid_checkout: false,
  public_profiles: true,
  chat_media: false,
  signup: true,
  maintenance_mode: false,
  age_gate: false,
};
